import {
  BookOpen,
  Captions,
  Expand,
  Info,
  MousePointer2,
  Pause,
  Play,
  Volume2,
  VolumeX,
} from "lucide-react";
import { useCallback, useEffect, useEffectEvent, useMemo, useRef, useState } from "react";
import {
  AnatomyScene,
  MODEL_META,
  preloadJourneyModels,
  type SelectedStructure,
} from "./components/AnatomyScene";
import { EvidencePanel } from "./components/EvidencePanel";
import { LayerPanel } from "./components/LayerPanel";
import { SourcesDrawer } from "./components/SourcesDrawer";
import { StageRail } from "./components/StageRail";
import { Timeline } from "./components/Timeline";
import {
  TOTAL_PLAYBACK_MS,
  getStageAtTime,
  sources,
  stages,
  stageStartTimes,
  type LayerId,
  type SneezeStage,
} from "./content/sneeze";

const ALL_LAYERS: LayerId[] = [
  "surface",
  "respiratory",
  "larynx",
  "mouth",
  "ocular",
  "nervous",
  "pressure",
];

function layersForStage(index: number): Record<LayerId, boolean> {
  const defaults = new Set(stages[index].defaultLayers);
  return Object.fromEntries(ALL_LAYERS.map((layer) => [layer, defaults.has(layer)])) as Record<
    LayerId,
    boolean
  >;
}

function readInitialScene() {
  const params = new URLSearchParams(window.location.search);
  const requestedStage = params.get("stage");
  const stageIndex = stages.findIndex((stage) => stage.id === requestedStage);
  const resolvedIndex = stageIndex >= 0 ? stageIndex : 0;
  const requestedFocus = params.get("focus");
  const focus = stages.some((stage) => stage.focus === requestedFocus)
    ? (requestedFocus as SneezeStage["focus"])
    : stages[resolvedIndex].focus;
  const requestedLayers = (params.get("layers") ?? "")
    .split(",")
    .filter((layer): layer is LayerId => ALL_LAYERS.includes(layer as LayerId));
  const sceneLayers = requestedLayers.length
    ? (Object.fromEntries(
        ALL_LAYERS.map((layer) => [layer, requestedLayers.includes(layer)]),
      ) as Record<LayerId, boolean>)
    : layersForStage(resolvedIndex);

  return {
    elapsedMs: stageStartTimes[resolvedIndex],
    isPlaying: stageIndex >= 0 && params.get("play") === "1",
    started: stageIndex >= 0,
    layers: sceneLayers,
    focus,
  };
}

function detectWebGL() {
  try {
    const canvas = document.createElement("canvas");
    return Boolean(canvas.getContext("webgl2") || canvas.getContext("webgl"));
  } catch {
    return false;
  }
}

function App() {
  const [initialScene] = useState(readInitialScene);
  const [elapsedMs, setElapsedMs] = useState(initialScene.elapsedMs);
  const [isPlaying, setIsPlaying] = useState(initialScene.isPlaying);
  const [started, setStarted] = useState(initialScene.started);
  const [speed, setSpeed] = useState(1);
  const [exploring, setExploring] = useState(false);
  const [cameraFocus, setCameraFocus] = useState<SneezeStage["focus"]>(initialScene.focus);
  const [layers, setLayers] = useState<Record<LayerId, boolean>>(initialScene.layers);
  const [surfaceOpacity, setSurfaceOpacity] = useState(0.16);
  const [selected, setSelected] = useState<SelectedStructure | null>(null);
  const [sourcesOpen, setSourcesOpen] = useState(false);
  const [narrationOn, setNarrationOn] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(() =>
    window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  );
  const [webglAvailable] = useState(detectWebGL);
  const previousStageIndex = useRef(getStageAtTime(initialScene.elapsedMs).index);
  const elapsedRef = useRef(initialScene.elapsedMs);

  const stageState = useMemo(() => getStageAtTime(elapsedMs), [elapsedMs]);
  const activeStage = stageState.stage;
  const activeIndex = stageState.index;
  const stageProgress = Math.min(
    1,
    Math.max(0, (elapsedMs - stageState.stageStartMs) / activeStage.playbackMs),
  );
  const structureOptions = [
    layers.surface ? MODEL_META.skin : null,
    layers.respiratory ? MODEL_META.lung : null,
    layers.respiratory ? MODEL_META.trachea : null,
    layers.larynx ? MODEL_META.larynx : null,
    layers.mouth ? MODEL_META.mouth : null,
    layers.ocular ? MODEL_META.eye : null,
  ].filter((structure): structure is SelectedStructure => Boolean(structure));

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => {
      setReducedMotion(media.matches);
      if (media.matches) setIsPlaying(false);
    };
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    elapsedRef.current = elapsedMs;
  }, [elapsedMs]);

  useEffect(() => {
    if (!isPlaying) return;
    let frame = 0;
    let previous = performance.now();

    const tick = (now: number) => {
      const delta = Math.min(now - previous, 100) * speed;
      previous = now;
      const next = Math.min(TOTAL_PLAYBACK_MS, elapsedRef.current + delta);
      elapsedRef.current = next;
      setElapsedMs(next);
      if (next >= TOTAL_PLAYBACK_MS) {
        setIsPlaying(false);
        return;
      }
      frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [isPlaying, speed]);

  useEffect(() => {
    if (activeIndex === previousStageIndex.current) return;
    previousStageIndex.current = activeIndex;
    setLayers(layersForStage(activeIndex));
    setSelected(null);
    setExploring(false);
    setCameraFocus(activeStage.focus);
  }, [activeIndex, activeStage.focus]);

  useEffect(() => {
    if (!narrationOn || !isPlaying || !("speechSynthesis" in window)) {
      window.speechSynthesis?.cancel();
      return;
    }

    const utterance = new SpeechSynthesisUtterance(activeStage.narration);
    utterance.rate = 0.9;
    utterance.pitch = 0.92;
    const voices = window.speechSynthesis.getVoices();
    utterance.voice =
      voices.find((voice) => voice.lang.startsWith("en") && /Samantha|Daniel|Alex/i.test(voice.name)) ??
      voices.find((voice) => voice.lang.startsWith("en")) ??
      null;
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(utterance);
    return () => window.speechSynthesis.cancel();
  }, [activeIndex, activeStage.narration, isPlaying, narrationOn]);

  const chooseStage = useCallback((index: number) => {
    setStarted(true);
    setElapsedMs(stageStartTimes[index]);
    setExploring(false);
    setSelected(null);
    setLayers(layersForStage(index));
    setCameraFocus(stages[index].focus);
  }, []);

  const togglePlaying = useCallback(() => {
    setStarted(true);
    setExploring(false);
    setSelected(null);
    if (!isPlaying && elapsedMs >= TOTAL_PLAYBACK_MS) setElapsedMs(0);
    setIsPlaying((current) => !current);
  }, [elapsedMs, isPlaying]);

  const enterJourney = () => {
    setStarted(true);
    setElapsedMs(0);
    setLayers(layersForStage(0));
    setCameraFocus(stages[0].focus);
    setExploring(false);
    setIsPlaying(!reducedMotion);
    // Let the first frame paint before the browser begins decoding the larger
    // later-stage anatomy files. The first two stages only need the skin and
    // procedural overlays, so this keeps the CTA response immediate.
    if (!reducedMotion) window.setTimeout(preloadJourneyModels, 2_000);
  };

  const enterExplore = () => {
    setStarted(true);
    setIsPlaying(false);
    setExploring(true);
    setLayers({
      surface: true,
      respiratory: true,
      larynx: true,
      mouth: true,
      ocular: false,
      nervous: false,
      pressure: false,
    });
  };

  const handleExplore = () => {
    if (!started) return;
    setExploring(true);
    setIsPlaying(false);
  };

  const toggleLayer = (layer: LayerId) => {
    setLayers((current) => ({ ...current, [layer]: !current[layer] }));
  };

  const toggleFullscreen = async () => {
    if (!document.fullscreenElement) await document.documentElement.requestFullscreen?.();
    else await document.exitFullscreen?.();
  };

  const onGlobalKeyDown = useEffectEvent((event: KeyboardEvent) => {
      if (event.code === "Escape") {
        setSourcesOpen(false);
        return;
      }

      const target = event.target as HTMLElement | null;
      const isInteractive = target?.matches(
        "button, a, input, select, textarea, [contenteditable='true']",
      );
      if (sourcesOpen || isInteractive) {
        return;
      }
      if (event.code === "Space") {
        event.preventDefault();
        togglePlaying();
      }
      if (event.code === "ArrowRight") {
        event.preventDefault();
        chooseStage(Math.min(stages.length - 1, activeIndex + 1));
      }
      if (event.code === "ArrowLeft") {
        event.preventDefault();
        chooseStage(Math.max(0, activeIndex - 1));
      }
  });

  useEffect(() => {
    window.addEventListener("keydown", onGlobalKeyDown);
    return () => window.removeEventListener("keydown", onGlobalKeyDown);
  }, []);

  return (
    <main className={`app-shell${started ? " is-started" : " is-intro"}`}>
      <div
        className="scene-canvas"
        aria-label="Interactive 3D anatomy scene"
        data-camera-focus={cameraFocus}
      >
        {webglAvailable ? (
          <AnatomyScene
            stageId={activeStage.id}
            focus={cameraFocus}
            stageProgress={stageProgress}
            layers={layers}
            surfaceOpacity={surfaceOpacity}
            exploring={exploring}
            reducedMotion={reducedMotion}
            motionActive={isPlaying && !exploring && !reducedMotion}
            onExplore={handleExplore}
            onSelect={setSelected}
          />
        ) : (
          <div className="webgl-fallback">
            <Info size={26} />
            <h2>3D rendering is unavailable</h2>
            <p>The evidence and stage descriptions remain available. Enable WebGL to inspect the anatomy.</p>
          </div>
        )}
      </div>
      <div className="atmosphere-grid" aria-hidden="true" />
      <div className="scene-vignette" aria-hidden="true" />
      <div className="noise-layer" aria-hidden="true" />

      <header className="topbar">
        <button
          type="button"
          className="brand"
          onClick={() => {
            setStarted(false);
            setIsPlaying(false);
          }}
          aria-label="Human Moment home"
        >
          <span className="brand__mark"><i /></span>
          <span>
            <strong>Human / Moment</strong>
            <small>Evidence-linked anatomy</small>
          </span>
        </button>

        <div className="topbar__scene">
          <strong>Inside a sneeze</strong>
          <span>Protective reflex · scene {String(activeStage.order).padStart(2, "0")} of 06</span>
        </div>

        <div className="topbar__actions">
          <span className="data-status"><i /> Static source of truth</span>
          <button
            type="button"
            className="icon-button"
            onClick={() => setNarrationOn((current) => !current)}
            aria-label={narrationOn ? "Turn narration off" : "Turn narration on"}
            title={narrationOn ? "Narration on" : "Narration off"}
          >
            {narrationOn ? <Volume2 size={16} /> : <VolumeX size={16} />}
          </button>
          <button type="button" className="topbar__source-button" onClick={() => {
            setSourcesOpen(true);
            setIsPlaying(false);
          }}>
            <BookOpen size={14} /> Sources
          </button>
          <button type="button" className="icon-button" onClick={toggleFullscreen} aria-label="Enter fullscreen" title="Fullscreen">
            <Expand size={16} />
          </button>
        </div>
      </header>

      {started && (
        <>
          <div className="left-column">
            <StageRail
              stages={stages}
              activeIndex={activeIndex}
              stageProgress={stageProgress}
              onChoose={chooseStage}
            />
            <LayerPanel
              layers={layers}
              surfaceOpacity={surfaceOpacity}
              onToggle={toggleLayer}
              onOpacityChange={setSurfaceOpacity}
            />
          </div>

          <div className="right-column">
            <EvidencePanel
              stage={activeStage}
              selected={selected}
              structureOptions={structureOptions}
              sources={sources}
              onSelectStructure={setSelected}
              onClearSelected={() => setSelected(null)}
              onOpenSources={() => {
                setSourcesOpen(true);
                setIsPlaying(false);
              }}
            />
          </div>

          <section className="scene-copy" key={activeStage.id} aria-live="polite">
            <span>{activeStage.eyebrow}</span>
            <h1>{activeStage.title}</h1>
            <p>{activeStage.summary}</p>
          </section>

          <div className="scene-hint">
            <MousePointer2 size={13} />
            <span>{exploring ? "Free exploration · drag, zoom, and select" : "Drag to pause and inspect freely"}</span>
          </div>

          <Timeline
            elapsedMs={elapsedMs}
            isPlaying={isPlaying}
            speed={speed}
            stages={stages}
            activeIndex={activeIndex}
            exploring={exploring}
            onTogglePlaying={togglePlaying}
            onSeek={(time) => {
              setElapsedMs(time);
              setExploring(false);
              setSelected(null);
            }}
            onPrevious={() => chooseStage(Math.max(0, activeIndex - 1))}
            onNext={() => chooseStage(Math.min(stages.length - 1, activeIndex + 1))}
            onSpeedChange={setSpeed}
            onResetCamera={() => setExploring(false)}
          />

          <div className="educational-notice">
            <Info size={11} /> Educational visualization · representative timing · not medical advice
          </div>
        </>
      )}

      {!started && (
        <section className="intro-overlay" aria-labelledby="intro-title">
          <div className="intro-overlay__index">Event 01 · Protective reflex</div>
          <h1 id="intro-title">
            What happens inside
            <em>a single sneeze?</em>
          </h1>
          <p>
            Follow the signal from nasal irritation to a millisecond release—through six aligned NIH anatomy layers and the human evidence behind every claim.
          </p>
          <div className="intro-overlay__actions">
            <button type="button" className="primary-action" onClick={enterJourney}>
              <Play size={15} fill="currentColor" /> Begin the 72-second journey
            </button>
            <button type="button" className="ghost-action" onClick={enterExplore}>
              Explore the anatomy
            </button>
          </div>
          <div className="intro-overlay__facts">
            <span><b>06</b> physiological stages</span>
            <span><b>NIH</b> reference anatomy</span>
            <span><b>0</b> API keys required</span>
          </div>
          <div className="intro-overlay__caption">
            <Captions size={13} /> Captions are always visible
          </div>
        </section>
      )}

      <SourcesDrawer open={sourcesOpen} onClose={() => setSourcesOpen(false)} />

      {isPlaying && (
        <button type="button" className="floating-pause" onClick={togglePlaying} aria-label="Pause sequence">
          <Pause size={13} fill="currentColor" /> Pause
        </button>
      )}
    </main>
  );
}

export default App;
